import * as React from "react";
import { useMemo } from "react";
import { Checkbox, Dropdown, Button } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dayjs } from "Config/dayjs";
import { EmailValue } from "../Components/EmailValue";
import { useTranslation, withTranslation, WithTranslation } from "react-i18next";
import i18n, { I18N_NS } from "Config/i18n";
import MediaQuery from "react-responsive";
import { extractIdFromUId } from "../utils/extractIdFromUId";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { observer } from "mobx-react";

interface EmailListItemProps {
  abstractRecipients?: API_AbstractRecipients;
  address: string | string[];
  correspondents: API_MailResponse["correspondents"];
  cc: string[];
  bcc: string[];
  subject: string;
  excerpt: string;
  isUnread: boolean;
  hasAttachments: boolean;
  isSelected: boolean;
  timestamp: number;
  importance: API_MAILREQUEST_IMPORTANCE;
  onCheckboxChange: () => void;
  onClick: () => void;
  style?: React.CSSProperties;
}

export const EmailImportance = ({ type }: { type: API_MAILREQUEST_IMPORTANCE }) => {
  return type === "HIGH" ? (
    <FontAwesomeIcon icon={["fas", "exclamation"]} style={{ color: "red" }} />
  ) : type === "LOW" ? (
    <FontAwesomeIcon icon={["fal", "long-arrow-down"]} style={{ color: "green" }} />
  ) : null;
};

export const EmailListItemAddress: React.FunctionComponent<
  Pick<EmailListItemProps, "address" | "cc" | "bcc" | "correspondents">
> = ({ address, cc = [], bcc = [], correspondents = {} }) => {
  if (!address || !address.slice) {
    address = [address as string];
  }
  const addressArray = [].concat(address.slice(), cc.slice(), bcc.slice()).filter((item) => !!item);

  return (
    <a className="email-address">
      {addressArray.length > 0 ? (
        addressArray.map((item, index) => <EmailValue key={index} uid={item} backupName={correspondents[item]} />)
      ) : (
        <span className="color-grey">{`[${i18n.t("No recipients", { ns: I18N_NS.PROJECT })}]`}</span>
      )}
    </a>
  );
};

const EmailRecipientsSeparator = () => <span className="email-recipients-separator">,</span>;

export const EmailListItemAbstractAddress: React.FunctionComponent<
  Pick<EmailListItemProps, "abstractRecipients" | "correspondents">
> = observer(({ abstractRecipients = {}, correspondents = {} }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const addressKeys = Object.keys(abstractRecipients) as unknown as [keyof API_AbstractRecipients];
  const groupNotExistName = t("Non-existent group");

  const groupsMap = useMemo(
    () => new Map(groupListStore.forMailAndPlot.map((group) => [group.id, group])),
    [groupListStore.forMailAndPlot]
  );

  const renderGroups = (groups: API_AbstractGroups[]) =>
    groups.map((group, idx) => {
      const abstractGroupId = extractIdFromUId(group.name).value;
      const existedGroup = groupsMap.get(+abstractGroupId);
      const isNotLast = groups.length !== idx + 1;

      return (
        <span key={idx} className="email-address-item">
          <FontAwesomeIcon icon="users" fixedWidth />
          <span style={{ marginLeft: 6 }}>{existedGroup?.name || groupNotExistName}</span>
          {isNotLast && <EmailRecipientsSeparator />}
        </span>
      );
    });

  const renderIndividuals = (individuals: string[]) =>
    individuals.map((item, idx) => {
      const backupName = !item.startsWith("@") ? { email: item } : correspondents[item];
      const isLast = individuals.length === idx + 1;

      return (
        <span key={item} className="email-address-item">
          <EmailValue uid={item} backupName={backupName} />
          {!isLast && <EmailRecipientsSeparator />}
        </span>
      );
    });

  const hasContent = addressKeys.some(
    (key) => abstractRecipients[key].groups.length > 0 || abstractRecipients[key].individuals.length > 0
  );

  if (!hasContent) {
    return <div className="flex color-grey">{`[${t("No recipients")}]`}</div>;
  }

  const content = addressKeys.map((key, index) => {
    const { groups, individuals } = abstractRecipients[key];
    const hasGroups = groups.length > 0;
    const hasIndividuals = individuals.length > 0;
    const showMidSeparator = hasGroups && hasIndividuals;
    const showPreSeparator = index > 0 && (hasGroups || hasIndividuals);

    return (
      <React.Fragment key={key}>
        {showPreSeparator && <EmailRecipientsSeparator />}
        {hasGroups && renderGroups(groups)}
        {showMidSeparator && <EmailRecipientsSeparator />}
        {hasIndividuals && renderIndividuals(individuals)}
      </React.Fragment>
    );
  });

  return <div className="flex">{content}</div>;
});

export const EmailListItemCheckbox: React.FunctionComponent<
  Pick<EmailListItemProps, "onCheckboxChange" | "isSelected">
> = ({ onCheckboxChange, isSelected }) => {
  return (
    <div
      className="email-cbox"
      onClick={(e) => {
        e.stopPropagation();
        onCheckboxChange();
      }}
    >
      <Checkbox checked={isSelected} />
    </div>
  );
};

export const EmailListItemPrio: React.FunctionComponent<Pick<EmailListItemProps, "importance">> = ({ importance }) => {
  return (
    <div className="email-prio">
      <EmailImportance type={importance} />
    </div>
  );
};

export const EmailListItemSubject: React.FunctionComponent<Pick<EmailListItemProps, "subject" | "excerpt">> = ({
  subject,
  excerpt,
}) => {
  return (
    <div className="email-snippet">
      <div>
        <span className="email-subject">{subject || `[${i18n.t("No subject", { ns: I18N_NS.PROJECT })}]`}</span>
        <span className="email-delimiter">&nbsp;&mdash;&nbsp;</span>
        {excerpt ? (
          <span className="email-excerpt" dangerouslySetInnerHTML={{ __html: excerpt }} />
        ) : (
          <span className="email-excerpt">{`[${i18n.t("No email text", { ns: I18N_NS.PROJECT })}]`}</span>
        )}
      </div>
    </div>
  );
};

export const EmailListItemAttach: React.FunctionComponent<Pick<EmailListItemProps, "hasAttachments">> = ({
  hasAttachments,
}) => {
  return <div className="email-attach">{hasAttachments && <FontAwesomeIcon icon={["fal", "paperclip"]} />}</div>;
};

export const EmailListItemDate: React.FunctionComponent<Pick<EmailListItemProps, "timestamp">> = ({ timestamp }) => {
  return <div className="email-date">{dayjs(+timestamp).format("lll")}</div>;
};

interface SortDropdownProps extends WithTranslation {
  sortBy: API_MAILPAGINATION_SORTBY;
  sortOrder: API_MAILPAGINATION_SORTORDER;
  onSort: (sortBy: API_MAILPAGINATION_SORTBY) => void;
}

const SortDropdown = withTranslation(I18N_NS.PROJECT)(
  class extends React.Component<SortDropdownProps> {
    sortByMap: { [key in API_MAILPAGINATION_SORTBY]: string } = {
      NAME: "Address",
      SUBJECT: "Subject",
      ATTACHMENT: "Attachment",
      DATE: "Date",
      IMPORTANCE: "Prio",
    };

    handleClick = ({ key }) => {
      this.props.onSort(key);
    };

    renderArrow = (order: API_MAILPAGINATION_SORTORDER) => {
      return order === "ASC" ? (
        <FontAwesomeIcon style={{ marginLeft: 5 }} icon={"sort-amount-up"} fixedWidth={true} />
      ) : (
        <FontAwesomeIcon style={{ marginLeft: 5 }} icon={"sort-amount-down"} fixedWidth={true} />
      );
    };

    render() {
      const { sortOrder, sortBy, t } = this.props;

      return (
        <div style={{ marginLeft: 5 }}>
          {t("Current sorting")}:
          <Dropdown
            menu={{
              items: [
                {
                  key: "NAME",
                  label: (
                    <>
                      {t("Address")}
                      {sortBy === "NAME" && sortOrder === "DESC" ? this.renderArrow("ASC") : this.renderArrow("DESC")}
                    </>
                  ),
                },
                {
                  key: "SUBJECT",
                  label: (
                    <>
                      {t("Subject")}
                      {sortBy === "SUBJECT" && sortOrder === "DESC"
                        ? this.renderArrow("ASC")
                        : this.renderArrow("DESC")}
                    </>
                  ),
                },
                {
                  key: "ATTACHMENT",
                  label: (
                    <>
                      {t("Attachment")}
                      {sortBy === "ATTACHMENT" && sortOrder === "DESC"
                        ? this.renderArrow("ASC")
                        : this.renderArrow("DESC")}
                    </>
                  ),
                },
                {
                  key: "DATE",
                  label: (
                    <>
                      {t("Date")}
                      {sortBy === "DATE" && sortOrder === "DESC" ? this.renderArrow("ASC") : this.renderArrow("DESC")}
                    </>
                  ),
                },
              ],
              className: "sorting-menu",
            }}
          >
            <Button className="mb-2" style={{ marginLeft: 10 }}>
              {t(`${this.sortByMap[sortBy]}`)}
              {this.renderArrow(sortOrder)}
            </Button>
          </Dropdown>
        </div>
      );
    }
  }
);

interface EmailListHeaderProps extends WithTranslation {
  emailType: EmailType;
  onCheckAll: () => void;
  indeterminate: boolean;
  checked: boolean;
  sortBy: API_MAILPAGINATION_SORTBY;
  sortOrder: API_MAILPAGINATION_SORTORDER;
  onSort: (sortBy: API_MAILPAGINATION_SORTBY) => void;
}

export const EmailListHeader = withTranslation(I18N_NS.PROJECT)(
  ({ sortBy, sortOrder, onSort }: EmailListHeaderProps) => {
    return (
      <MediaQuery maxWidth={767}>
        {(matches) => (matches ? <SortDropdown sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} /> : null)}
      </MediaQuery>
    );
  }
);
